<!--
  @component CredentialsIO — P4 effecting lens (credentials lifecycle).
  chain:  ctx.deployment.dispatch("credentials.*", …) → this → ctx.credentials
  effects: rotate-as-a-deploy (dual-read overlap window). The credential
           SOURCE adapters live execution-plane; the browser never sees
           raw values — only request rotation through the command port.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { CREDENTIALS_IO_KEY } from "./deployment-io.js";
  import type { CredentialsIOContext } from "./deployment-io.js";
  export { CREDENTIALS_IO_KEY };
  export type { CredentialsIOContext };
  export function useCredentialsIO(): CredentialsIOContext {
    const ctx = getContext<CredentialsIOContext | undefined>(CREDENTIALS_IO_KEY);
    if (!ctx) throw new Error("useCredentialsIO() called outside of a CredentialsIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: CredentialsIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("credentials.")); },
    rotate: (credentialRef, options?) =>
      dispatchOp("credentials.rotate", { credentialRef, options }),
  } satisfies CredentialsIOContext;

  setContext(CREDENTIALS_IO_KEY, ctx);
</script>
{@render children?.()}
